/**
 * Trunnion AI - Template Card Component
 * Displays a workflow template in the gallery grid with name, description,
 * node count, classification tag, category badge, use count, and action button.
 */

import { GitBranch, Users, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import type { WorkflowTemplate, TemplateCategory } from '@/types/template.types';
import type { ClassificationLevel } from '@/types';

interface TemplateCardProps {
  template: WorkflowTemplate;
  onUseTemplate: (template: WorkflowTemplate) => void;
  onPreview: (template: WorkflowTemplate) => void;
}

const CATEGORY_BADGE_VARIANT: Record<
  TemplateCategory,
  'default' | 'success' | 'warning' | 'error' | 'info' | 'purple'
> = {
  intelligence: 'purple',
  logistics: 'info',
  cyber: 'error',
  admin: 'warning',
  custom: 'default',
};

const CATEGORY_LABELS: Record<TemplateCategory, string> = {
  intelligence: 'Intelligence',
  logistics: 'Logistics',
  cyber: 'Cyber',
  admin: 'Admin',
  custom: 'Custom',
};

export function TemplateCard({
  template,
  onUseTemplate,
  onPreview,
}: TemplateCardProps) {
  const categoryLabel =
    CATEGORY_LABELS[template.category] ?? template.category ?? 'Custom';
  const categoryVariant =
    CATEGORY_BADGE_VARIANT[template.category] ?? 'default';

  return (
    <Card
      hover
      padding="none"
      className="flex flex-col"
      onClick={() => onPreview(template)}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">
            {template.name}
          </h3>
        </div>
        <ClassificationTag
          level={
            (template.classificationLevel as ClassificationLevel) ??
            'UNCLASSIFIED'
          }
          size="sm"
        />
      </div>

      {/* Description */}
      <div className="px-4 pb-3 flex-1">
        <p className="text-xs text-trunnion-slate-400 line-clamp-2 leading-relaxed">
          {template.description || 'No description available.'}
        </p>
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-3 px-4 pb-3">
        <Badge variant={categoryVariant}>{categoryLabel}</Badge>
        <span className="flex items-center gap-1 text-xs text-trunnion-slate-500">
          <GitBranch className="h-3 w-3" />
          {template.nodeCount} nodes
        </span>
        <span className="flex items-center gap-1 text-xs text-trunnion-slate-500">
          <Users className="h-3 w-3" />
          {template.useCount} uses
        </span>
      </div>

      {/* Action footer */}
      <div className="border-t border-trunnion-slate-700 px-4 py-3">
        <Button
          size="sm"
          variant="primary"
          className="w-full"
          icon={<ArrowRight className="h-3.5 w-3.5" />}
          onClick={(e) => {
            e.stopPropagation();
            onUseTemplate(template);
          }}
        >
          Use Template
        </Button>
      </div>
    </Card>
  );
}
